import {
  Component,
  Inject,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheet, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { NgClass } from '@angular/common';
import { ChatService } from '@services/chat-service/chat-service';
import { ConnectionService } from '@services/connection-service';
import { UnpaidPendingCallService } from '@services/pending-call-banner-service';
import { CommonUtilityHelper } from '@services/common-utility-helper/common-utility-helper';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { ChatChannel, ChatMessage } from '@services/chat-service/chat-service.types';
import { EventLoggerService } from '@services/event-logger-service';
import { LanguageService } from '@services/language-service/language.service';
import { VOICE_INPUT_STATE, VoiceInputService, VoiceInputState } from '@services/voice-input-service';
import { appendTranscript, scrollTextFieldToEnd } from '@services/voice-input-service/text-field.utils';
import { VoiceMicInputComponent } from '@components/voice-mic-input/voice-mic-input.component';
import { AppConfig } from '../../../app.config';
import { PRIMARY_CHANNELS } from '@services/event-logger-service/event-logger.constant';
import { ApiClientConstant } from "@cureskin/api-client";

// Constants
const CALL_CONFIRMED = 'doctorCallConfirmed';

// Interfaces
interface ExtraQuestion {
  id: number;
  englishQuestion: string;
  hindiQuestion: string;
  questionLanguageString: string;
  answerLanguageString: string;
}

interface StoredQAPair {
  afterMessageId: string | null;
  question: string;
  answer: string;
}

export const DEFAULT_QAs: Omit<ExtraQuestion, 'id'>[] = [
  { englishQuestion: 'How is my kit designed?',    hindiQuestion: 'मेरी किट कैसे बनाई गई है?',    questionLanguageString: 'wyLqPc0WEl', answerLanguageString: '4bU6nDMM3I' },
  { englishQuestion: 'When will I see results?',   hindiQuestion: 'मुझे नतीजे कब दिखेंगे?',        questionLanguageString: 'lxpPDO5auY', answerLanguageString: 'NpEGUnVsXy' },
  { englishQuestion: 'How long is the treatment?', hindiQuestion: 'इलाज कितने समय का है?',          questionLanguageString: 'AHOQ3cNzwp', answerLanguageString: 'bu5dZvFQB7' },
  { englishQuestion: 'What does the doctor do?',   hindiQuestion: 'डॉक्टर क्या करते हैं?',          questionLanguageString: '9tvPLQcEs0', answerLanguageString: 'CxdNS8y3Bq' },
  { englishQuestion: 'What are monthly checkups?', hindiQuestion: 'मासिक चेकअप क्या होते हैं?',    questionLanguageString: '6yXQMxi5D1', answerLanguageString: 'EXDsyZ3vK5' },
  { englishQuestion: 'Why is photo needed?',       hindiQuestion: 'फोटो की ज़रूरत क्यों है?',       questionLanguageString: '5peyY5oAZP', answerLanguageString: 'OEVivNqgGA' },
];

@Component({
  selector: 'app-user-query-chat-bottomsheet',
  templateUrl: './user-query-chat-bottomsheet.component.html',
  standalone: true,
  imports: [NgClass, VoiceMicInputComponent],
  providers: [ChatService],
})
export class UserQueryChatBottomsheetComponent implements OnInit, OnDestroy {
  // ViewChild decorators
  @ViewChild('scrollContainer', { static: false }) scrollContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('msgInput', { static: false }) msgInput!: ElementRef<HTMLInputElement>;

  // Public properties
  user: Table.User;
  allChats: ChatMessage[] = [];
  extraQuestions: ExtraQuestion[] = [];
  treatmentSession: any;
  latestMessage: ChatMessage | null = null;
  showThinking: boolean = false;
  loadingOldMessages: boolean = false;
  initialLoading: boolean = false;
  displayLanguage: string;
  /** Mirrors the uncontrolled textarea so the send button and mic tooltip can react to it. */
  draft: string = '';
  voiceMicSupported: boolean = false;
  voiceTooltipDismissed: boolean = false;

  // Private properties
  private selectedQuestionId: number | null = null;
  private readonly ASKED_KEY = 'askedExtraQuestions';
  private readonly subscriptions: Subscription[] = [];
  private _previousChatsLength: number | null = null;
  private isScrolledToTop = false;
  private draftContainsVoice: boolean = false;

  constructor(
    @Inject(MAT_BOTTOM_SHEET_DATA) public data: any,
    private readonly appConfig: AppConfig,
    private readonly chatService: ChatService,
    private readonly bottomSheetRef: MatBottomSheetRef<UserQueryChatBottomsheetComponent>,
    private readonly eventLogger: EventLoggerService,
    private readonly connectionService: ConnectionService,
    private readonly pendingCallService: UnpaidPendingCallService,
    public commonUtilityHelper: CommonUtilityHelper,
    public readonly  languageService: LanguageService,
    private readonly zone: NgZone,
    private readonly voiceInputService: VoiceInputService
  ) {
    this.setupBackdropHandler();
  }

  async ngOnInit(): Promise<void> {
    this.initialLoading = true;
    this.user = this.data.user;
    this.displayLanguage = this.languageService.getDisplayLanguage(this.user?.get('languagePreference') ?? this.appConfig.Shared.Languages.EN);
    this.voiceInputService.reset();
    void this.resolveVoiceMicSupport();
    try {
      await this.initializeChat();
    } catch (error) {
      console.error('Error during initialization:', error);
    } finally {
      this.finishInitialization();
    }
    this.eventLogger.cleverTapEvent('pageOpen', JSON.stringify({ name: 'regimen_unpaid_user_ai_chat_bottomsheet_rendered' }), PRIMARY_CHANNELS);
  }

  ngOnDestroy(): void {
    this.cleanup();
  }

  async sendMessage(text: string): Promise<void> {
    const message = text.trim();
    // Clear before the empty guard so a whitespace-only draft doesn't linger in the box.
    this.handleMessageInput();
    if (!message) return;

    if (this.draftContainsVoice) {
      this.voiceInputService.notifyMessageSentWithVoice(message);
      this.draftContainsVoice = false;
    }
    await this.processMessageSend(message);
    await this.updateQuestionState();
  }

  /** Whether the mic can be offered right now — hidden while the input itself is disabled. */
  get isVoiceMicVisible(): boolean {
    if (!this.voiceMicSupported) return false;
    return !this.inputDisabled;
  }

  get inputDisabled(): boolean {
    return this.showThinking || !this.allChats.length;
  }

  get showVoiceTooltip(): boolean {
    if (!this.isVoiceMicVisible) return false;
    if (this.voiceTooltipDismissed) return false;
    return !this.draft;
  }

  onDraftChange(value: string): void {
    this.draft = value;
    this.dismissVoiceTooltip();
  }

  dismissVoiceTooltip(): void {
    this.voiceTooltipDismissed = true;
  }

  onVoiceTranscript(transcript: string): void {
    if (!transcript) return;
    const input = this.msgInput?.nativeElement;
    const next = appendTranscript(input?.value ?? this.draft, transcript);
    if (input) {
      input.value = next;
      scrollTextFieldToEnd(input);
    }
    this.draft = next;
    this.draftContainsVoice = true;
    this.dismissVoiceTooltip();
  }

  onVoiceStateChange(state: VoiceInputState): void {
    if (state === VOICE_INPUT_STATE.LISTENING) this.dismissVoiceTooltip();
  }

  get isVoiceListening(): boolean {
    return this.voiceInputService.state === VOICE_INPUT_STATE.LISTENING;
  }

  private async resolveVoiceMicSupport(): Promise<void> {
    const supported = await this.voiceInputService.isMicAvailable();
    this.zone.run((): void => {
      this.voiceMicSupported = supported;
    });
  }

  async handleInputClick(chat: ChatMessage, inputObj: any): Promise<void> {
    if (!inputObj?.text) return;
    await this.sendMessage(inputObj.text);
  }

  async askExtraQuestion(question: ExtraQuestion): Promise<void> {
    this.selectedQuestionId = question.id;

    const questionText = this.displayLanguage === ApiClientConstant.LanguageString.HI
      ? question.hindiQuestion
      : question.englishQuestion;

    // Capture the ID of the last real message before adding any local messages.
    // Used as an anchor so the Q&A pair can be re-inserted at the right position on reopen.
    const afterMessageId = this.allChats.at(-1)?.id ?? null;

    // Show user message immediately as a local placeholder for instant feedback
    this.chatService.addLocalMessages([this.createLocalChatMessage('USER', questionText)]);

    // Show thinking; enforce a minimum 2-second display duration
    this.chatService.setThinking(true);
    const minDelay = new Promise<void>(resolve => setTimeout(resolve, 2000));

    // Run fetch and minimum delay in parallel; allSettled always waits for both
    const [fetchResult] = await Promise.allSettled([
      this.fetchAnswerWithFallback(question),
      minDelay,
    ]);

    this.chatService.setThinking(false);

    const answerText = fetchResult.status === 'fulfilled' ? fetchResult.value : '';
    if (answerText) {
      this.chatService.addLocalMessages([this.createLocalChatMessage('BOT', answerText)]);
      // Persist to sessionStorage so the Q&A pair is restored on next bottomsheet open
      this.storeLocalQAPair({ afterMessageId, question: questionText, answer: answerText });
    }

    await this.updateQuestionState();
  }

  closePopup(): void {
    this.bottomSheetRef.dismiss();
  }

  onScroll(): void {
    if (!this.scrollContainer?.nativeElement) return;
    this.handleScrollState();
  }

  convertTextToHtml(text: string): string {
    return this.commonUtilityHelper.convertTextToHtml(text);
  }

  private async initializeChat(): Promise<void> {
    await this.loadExperimentsAndQuestions();
    await this.filterAskedQuestions();
    await this.loadUserAndTreatmentSession();

    this.setupSubscriptions();

    await this.loadChatHistory();
    await this.handleInitialMessage();
  }

  private setupBackdropHandler(): void {
    this.bottomSheetRef.backdropClick().pipe(take(1)).subscribe(() => {
      this.zone.run(() => {
        if (this.msgInput?.nativeElement) {
          this.msgInput.nativeElement.disabled = true;
          this.msgInput.nativeElement.blur();
        }
      });
    });
  }

  private finishInitialization(): void {
    this.zone.run(() => {
      this.initialLoading = false;
      setTimeout(() => this.scrollToBottom(), 0);
    });
  }

  private handleMessageInput(): void {
    this.zone.run(() => {
      this.draft = '';
      if (this.msgInput?.nativeElement) {
        this.msgInput.nativeElement.blur();
        this.msgInput.nativeElement.value = '';
      }
    });
  }

  private async processMessageSend(text: string): Promise<void> {
    const message = text.trim();
    // Local Q&A messages have no className — ChatService.createReplyMessage needs a real
    // Parse object's className to construct the reply. Skip local messages when looking
    // for the last message reference.
    const lastMessage = [...this.allChats].reverse().find(m => !!m.className) ?? null;

    await this.chatService.sendMessage(this.buildChannel(), lastMessage, message);
  }

  private buildChannel(): ChatChannel {
    return {
      user: this.user,
      parent: this.treatmentSession,
      parentField: 'treatmentSession',
      fetchChats: (payload: any): Promise<any[]> => this.connectionService.fetchTreatmentChat(payload),
    };
  }

  private async updateQuestionState(): Promise<void> {
    if (this.selectedQuestionId === null) return;

    this.extraQuestions = this.extraQuestions.filter(
      (question: ExtraQuestion): boolean => question.id !== this.selectedQuestionId
    );

    const stored = sessionStorage.getItem(this.ASKED_KEY);
    const askedIds: number[] = stored ? JSON.parse(stored) : [];
    sessionStorage.setItem(
      this.ASKED_KEY,
      JSON.stringify([...askedIds, this.selectedQuestionId])
    );

    this.selectedQuestionId = null;
  }

  private scrollToBottom(): void {
    if (!this.scrollContainer?.nativeElement) return;

    this.zone.run(() => {
      setTimeout(() => {
        this.scrollContainer.nativeElement.scrollTop = this.scrollContainer.nativeElement.scrollHeight;
      }, 0);
    });
  }

  private handleScrollState(): void {
    const { scrollTop } = this.scrollContainer.nativeElement;
    this.isScrolledToTop = scrollTop === 0;

    if (this.isScrolledToTop && !this.loadingOldMessages) {
      this.loadMoreMessages();
    }
  }

  private async loadExperimentsAndQuestions(): Promise<void> {
    const experiments = await this.connectionService.findUserActiveExperiments();
    const questionExperiment = experiments?.find((exp: any) => exp.key === 'unpaid_user_llm_bot');
    this.loadExtraQuestions(questionExperiment);
  }

  private loadExtraQuestions(experiment: any): void {
    const qas: any[] = experiment?.variant?.defaultQAs ?? DEFAULT_QAs;
    this.extraQuestions = qas.map((qa: any, index: number) => ({ id: index, ...qa }));
  }

  private async filterAskedQuestions(): Promise<void> {
    const stored = sessionStorage.getItem(this.ASKED_KEY);
    const askedIds: number[] = stored ? JSON.parse(stored) : [];
    this.extraQuestions = this.extraQuestions.filter((q) => askedIds.indexOf(q.id) === -1);
  }

  private async loadUserAndTreatmentSession(): Promise<void> {
    this.user = await this.connectionService.getActingUser();
    const treatmentSessionObj = await this.connectionService.findTreatmentSession();
    this.treatmentSession = {
      __type: 'Pointer',
      className: 'TreatmentSession',
      objectId: treatmentSessionObj?.treatmentSession.objectId,
    };
  }

  private async loadChatHistory(): Promise<void> {
    await this.chatService.loadChatHistory(this.buildChannel());
    this.restoreLocalQAs();
  }

  private async loadMoreMessages(): Promise<void> {
    await this.chatService.loadMoreMessages(this.buildChannel());
  }

  private setupSubscriptions(): void {
    this.subscriptions.push(
      this.chatService.allChats$.subscribe(chats => this.handleChatsUpdate(chats)),
      this.chatService.latestMessage$.subscribe(message => this.handleLatestMessage(message)),
      this.chatService.showThinking$.subscribe(thinking => this.handleThinkingState(thinking)),
      this.chatService.loadingOldMessages$.subscribe(loading => this.handleLoadingState(loading))
    );
  }

  private handleChatsUpdate(chats: ChatMessage[]): void {
    this.zone.run(() => {
      this.allChats = chats;
      if (!this.loadingOldMessages && chats.length > 0 && (!this._previousChatsLength || chats.length > this._previousChatsLength)) {
        this.scrollToBottom();
      }
      this._previousChatsLength = chats.length;
    });
  }

  private handleLatestMessage(message: ChatMessage | null): void {
    this.zone.run(() => {
      if (message?.get('questionId') === CALL_CONFIRMED) {
        this.pendingCallService.notifyCallCreated();
      }
      this.latestMessage = message;
    });
  }

  private handleThinkingState(thinking: boolean): void {
    this.zone.run(() => {
      this.showThinking = thinking;
      if (thinking && this.msgInput?.nativeElement) {
        this.msgInput.nativeElement.blur();
      }
    });
  }

  private handleLoadingState(loading: boolean): void {
    this.zone.run(() => {
      this.loadingOldMessages = loading;
    });
  }

  private async handleInitialMessage(): Promise<void> {
    if (!this.data?.initialMessage?.trim()) {
      this.scrollToBottom();
      return;
    }

    const lastMessage = this.allChats.at(-1) ?? null;
    if (lastMessage) {
      await this.chatService.sendMessage(this.buildChannel(), lastMessage, this.data.initialMessage.trim());
      delete this.data.initialMessage;
    }
    this.scrollToBottom();
  }

  private async fetchAnswerWithFallback(question: ExtraQuestion): Promise<string> {
    const extract = (strings: any[]): string =>
      strings?.[0]?.get(this.displayLanguage) || strings?.[0]?.get(ApiClientConstant.LanguageString.EN) || '';

    try {
      const strings = await this.connectionService.getLanguageStringsById([question.answerLanguageString]);
      const text = extract(strings);
      if (text) return text;
    } catch { /* fall through to DEFAULT_QAs retry */ }

    const defaultQA = DEFAULT_QAs.find(qa => qa.englishQuestion === question.englishQuestion);
    if (!defaultQA || defaultQA.answerLanguageString === question.answerLanguageString) return '';

    try {
      const fallbackStrings = await this.connectionService.getLanguageStringsById([defaultQA.answerLanguageString]);
      return extract(fallbackStrings);
    } catch {
      return '';
    }
  }

  private restoreLocalQAs(): void {
    const pairs = this.getStoredLocalQAs();
    if (!pairs.length) return;

    const merged = [...this.allChats];
    for (const pair of pairs) {
      const anchorIdx = pair.afterMessageId
        ? merged.findIndex(m => m.id === pair.afterMessageId)
        : -1;
      const insertAt = anchorIdx !== -1 ? anchorIdx + 1 : merged.length;
      merged.splice(
        insertAt, 0,
        this.createLocalChatMessage('USER', pair.question),
        this.createLocalChatMessage('BOT', pair.answer)
      );
    }

    this.chatService.replaceMessages(merged);
  }

  private storeLocalQAPair(pair: StoredQAPair): void {
    const key = this.getLocalQAsStorageKey();
    const stored = sessionStorage.getItem(key);
    const pairs: StoredQAPair[] = stored ? JSON.parse(stored) : [];
    pairs.push(pair);
    sessionStorage.setItem(key, JSON.stringify(pairs));
  }

  private getStoredLocalQAs(): StoredQAPair[] {
    const stored = sessionStorage.getItem(this.getLocalQAsStorageKey());
    return stored ? JSON.parse(stored) : [];
  }

  private getLocalQAsStorageKey(): string {
    return `localQAPairs_${this.user?.id}_${this.treatmentSession?.objectId}`;
  }

  private createLocalChatMessage(owner: 'USER' | 'BOT', text: string): any {
    const data: Record<string, any> = { Owner: owner, Message: text };
    return { id: `local_${Date.now()}_${Math.random()}`, get: (key: string) => data[key] };
  }

  private cleanup(): void {
    this.chatService.stopBotReplyTimer();
    this.voiceInputService.reset();
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  protected readonly ApiClientConstant = ApiClientConstant;
}
